import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  useTheme
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

interface EmployeeData {
  servidor: {
    id: number;
    nome_completo: string;
    cpf: string;
    email?: string;
    telefone?: string;
  };
  tem_contrato: boolean;
  matriculas: Array<{
    id: number;
    matricula: string;
    salario_julho_07: number;
    valor_total_2007: number;
    salario_julho_08: number;
    valor_total_2008: number;
    salario_julho_09: number;
    valor_total_2009: number;
    salario_julho_10: number;
    valor_total_2010: number;
    total_geral: number;
    honorarios_adv_25: number;
    igeprev_11: number;
    valor_liquido_servidor: number;
  }>;
  totais: {
    total_geral: number;
    total_honorarios: number;
    total_igeprev: number;
    total_liquido: number;
  };
}

interface EmployeeDetailsProps {
  data: EmployeeData;
  onRequestDocument: () => void;
  onGoToDocuments: () => void;
}

const EmployeeDetails: React.FC<EmployeeDetailsProps> = ({ data, onRequestDocument }) => {
  const theme = useTheme();

  const totalLiquido =
    data.totais?.total_liquido ??
    data.matriculas.reduce((sum, m) => sum + (m.valor_liquido_servidor || 0), 0);

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined || value === null || isNaN(value)) {
      return 'R$ 0,00';
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const actionRows = data.matriculas.map((m) => ({
    action: `Matrícula ${m.matricula}`,
    value: m.valor_liquido_servidor,
  }));

  return (
    <Paper elevation={3} sx={{ p: 4, borderRadius: 3, maxWidth: 600, mx: 'auto' }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            backgroundColor: theme.palette.primary.main,
            borderRadius: 2,
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AccountBalanceIcon sx={{ color: 'white', fontSize: 40 }} />
        </Box>
      </Box>

      <Typography variant="h5" align="center" gutterBottom>
        Consultar valor a receber
      </Typography>
      <Typography variant="h4" align="center" gutterBottom>
        Parabéns, {data.servidor.nome_completo}!
      </Typography>

      <Typography variant="subtitle1" align="center" gutterBottom>
        Você possui um crédito aproximado de
      </Typography>
      <Typography variant="h3" align="center" color="secondary.main" gutterBottom>
        {formatCurrency(totalLiquido)}
      </Typography>

      {actionRows.length > 0 && (
        <Box sx={{ my: 3 }}>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ação</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Valor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {actionRows.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{row.action}</TableCell>
                    <TableCell align="right">{formatCurrency(row.value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Box sx={{ textAlign: 'center', mt: 3 }}>
        <Button
          variant="contained"
          size="large"
          onClick={onRequestDocument}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            fontSize: '1.1rem',
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #1976d2 30%, #0d47a1 90%)',
            '&:hover': { background: 'linear-gradient(45deg, #0d47a1 30%, #002171 90%)' }
          }}
        >
          Solicitar Documentos
        </Button>
      </Box>
    </Paper>
  );
};

export default EmployeeDetails;
